import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_LICENSE_FORM } from '../utils/licenseConstants';
import { validateLicenseForm, hasValidationErrors } from '../utils/licenseValidation';
import {
  buildCreateLicensePayload,
  buildUpdateLicensePayload,
  licenseToFormValues,
} from '../utils/normalizeLicense';
import { licenseService } from '../services/licenseService';

function mapServerErrors(serverErrors = {}) {
  const mapped = { ...serverErrors };

  if (serverErrors.permission_profile_id) {
    mapped.permissionProfileId = Array.isArray(serverErrors.permission_profile_id)
      ? serverErrors.permission_profile_id[0]
      : serverErrors.permission_profile_id;
  }

  if (serverErrors.code) {
    mapped.alias = Array.isArray(serverErrors.code)
      ? serverErrors.code[0]
      : serverErrors.code;
  }

  return mapped;
}

export default function useLicenseForm({ license = null, onSuccess, onClose, open = false }) {
  const isEditMode = Boolean(license?.id);
  const [form, setForm] = useState(() =>
    license ? licenseToFormValues(license) : { ...DEFAULT_LICENSE_FORM }
  );
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [validationAttempt, setValidationAttempt] = useState(0);

  useEffect(() => {
    if (!open) {
      return;
    }
    setForm(license ? licenseToFormValues(license) : { ...DEFAULT_LICENSE_FORM });
    setErrors({});
    setIsDirty(false);
    setValidationAttempt(0);
  }, [open, license]);

  const resetForm = useCallback(() => {
    setForm(license ? licenseToFormValues(license) : { ...DEFAULT_LICENSE_FORM });
    setErrors({});
    setIsDirty(false);
    setValidationAttempt(0);
  }, [license]);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose?.();
  }, [onClose, resetForm]);

  const submit = useCallback(async () => {
    const validationErrors = validateLicenseForm(form);
    setErrors(validationErrors);

    if (hasValidationErrors(validationErrors)) {
      setValidationAttempt((attempt) => attempt + 1);
      return { success: false, validationFailed: true };
    }

    setIsSubmitting(true);

    try {
      const payload = isEditMode
        ? buildUpdateLicensePayload(form)
        : buildCreateLicensePayload(form);

      const res = isEditMode
        ? await licenseService.updateLicense(license.id, payload)
        : await licenseService.createLicense(payload);

      if (res?.status === 200 || res?.status === 201) {
        const saved = res?.data?.data ?? res?.data;
        resetForm();
        onSuccess?.(saved, { isEditMode });
        onClose?.();
        return { success: true, data: saved };
      }

      const serverErrors = res?.data?.errors ?? res?.data;
      if (serverErrors && typeof serverErrors === 'object') {
        setErrors(mapServerErrors(serverErrors));
        setValidationAttempt((attempt) => attempt + 1);
      }

      throw new Error(
        res?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} license`
      );
    } catch (err) {
      const responseErrors = err?.response?.data;
      if (responseErrors && typeof responseErrors === 'object') {
        setErrors(mapServerErrors(responseErrors));
        setValidationAttempt((attempt) => attempt + 1);
      }

      const message =
        err?.response?.data?.message ||
        err?.message ||
        `Failed to ${isEditMode ? 'update' : 'create'} license`;
      return { success: false, message };
    } finally {
      setIsSubmitting(false);
    }
  }, [form, isEditMode, license, onClose, onSuccess, resetForm]);

  return {
    form,
    errors,
    isSubmitting,
    isEditMode,
    isDirty,
    validationAttempt,
    updateField,
    submit,
    resetForm,
    handleClose,
  };
}
